import { inject, Injectable } from "@angular/core";
import { Action, Selector, State, StateContext, Store } from "@ngxs/store";
import { FileModel, FileStateModel } from "./file-manager.model";
import { FileManagerActions } from "./file-manager.actions";
import { FileManagerService } from "../file-manager.service";
import { catchError, map, of, Subject, tap } from "rxjs";
import { MatDialog } from "@angular/material/dialog";
import { FileViewComponent } from "../file-view/file-view.component";
import { SharedGridState } from "../../ag-grid/shared-grid-state/shared-grid.state";
import { OnSetSharedGridRowData } from "../../ag-grid/shared-grid-state/shared-grid.actions";
import { produce } from "immer";

const DEFAULT_FILE_MANAGER_STATE: FileStateModel = {
    gridDocument: undefined,
    allFiles: undefined,
    selectedFile: undefined,
    fileDetailsDrawerState: false,
    fileUploadDrawerState: false,
    uploadedFiles: [],
};

@State<FileStateModel>({
    name: 'fileManagerState',
    defaults: DEFAULT_FILE_MANAGER_STATE,
})

@Injectable()
export class FileManagerState {
    private _dialog = inject(MatDialog);
    private _fileManagerService = inject(FileManagerService);
    private _store = inject(Store);
    @Action(FileManagerActions.SetGridDocument)
    setGridDocument(ctx: StateContext<FileStateModel>, action: FileManagerActions.SetGridDocument) {
        ctx.patchState({ gridDocument: action.payload });
    }

    @Action(FileManagerActions.GetFiles)
    getFiles(ctx: StateContext<FileStateModel>) {
        const gridId = ctx.getState().gridDocument._id;
        return this._fileManagerService.getAllDBFiles(gridId).pipe(
            map((result: FileModel[]) => {
                return result.map((file) => ({
                    ...file,
                    sizeReadable: this._fileManagerService.convertBytes(file.size)
                }));
            }),
            tap((result: FileModel[]) => {
                ctx.patchState({ allFiles: result });  // Update state with the fetched files
            })
        );
        // Fetch files from server or local storage

    }

    @Action(FileManagerActions.SetSelectedFile)
    setSelectedFile(ctx: StateContext<FileStateModel>, action: FileManagerActions.SetSelectedFile) {
        ctx.patchState({ selectedFile: action.payload });
    }

    @Action(FileManagerActions.SetFileDetailsDrawerState)
    setFileDetailsDrawerState(ctx: StateContext<FileStateModel>, action: FileManagerActions.SetFileDetailsDrawerState) {
        ctx.patchState({ fileDetailsDrawerState: action.payload });
    }

    @Action(FileManagerActions.SetFileUploadDrawerState)
    setFileUploadDrawerState(ctx: StateContext<FileStateModel>, action: FileManagerActions.SetFileUploadDrawerState) {
        ctx.patchState({ fileUploadDrawerState: action.payload });
    }

    @Action(FileManagerActions.UploadFile)
    uploadFile(ctx: StateContext<FileStateModel>, action: FileManagerActions.UploadFile) {
        const gridDocument = ctx.getState().gridDocument;
        action.payload.append('documentId', gridDocument._id);
        action.payload.append('documentReference', gridDocument.documentReference);
        action.payload.append('documentType', gridDocument.documentType);

        // return this._fileManagerService.uploadFile(action.payload)
        const uploadProgress$ = new Subject<any>();

        // Call the service and emit events manually
        this._fileManagerService.uploadFile(action.payload).subscribe({
            next: (event) => {
                uploadProgress$.next(event); // Emit events (progress, response, etc.)
            },
            error: (err) => {
                uploadProgress$.error(err); // Emit error
            },
            complete: () => {
                uploadProgress$.complete(); // Complete the Subject
            },
        });

        // Return the Subject as an observable
        return uploadProgress$.asObservable();
    }

    @Action(FileManagerActions.DeleteFile)
    deleteFile(ctx: StateContext<FileStateModel>, action: FileManagerActions.DeleteFile) {
        return this._fileManagerService.deleteFile(action.payload).pipe(
            catchError((error) => {
                console.error('Error deleting file:', error);
                return of(error);
            }),
            tap((response: boolean) => {
                if (response) {
                    const state = ctx.getState();
                    const remainingFiles = state.allFiles.filter((file: FileModel) => file._id !== action.payload);
                    ctx.patchState({
                        selectedFile: undefined,
                        fileDetailsDrawerState: false,
                        allFiles: remainingFiles
                    });

                    if (remainingFiles.length === 0) {

                        const documentId = state.gridDocument.documentId;
                        const gridUUIDs = this._store.selectSnapshot(SharedGridState.getGridReferenceUUIDs);
                        const gridConfig = this._store.selectSnapshot(SharedGridState.getGridConfig);
                        const rowData = gridConfig[gridUUIDs.firstGridUUID].rowData;

                        const updatedRowData = rowData.map(row => {
                            if (row._id === documentId) {
                                return { ...row, hasFiles: false };
                            }
                            return row;
                        });

                        ctx.dispatch(new OnSetSharedGridRowData({
                            gridUUID: gridUUIDs.firstGridUUID,
                            payload: updatedRowData,
                            update: true
                        }));
                    }
                }
            })
        );
    }

    @Selector()
    static getAllFiles(state: FileStateModel) {
        return state.allFiles;
    }

    @Selector()
    static getFileDetails(state: FileStateModel) {
        return state.selectedFile;

    }

    @Selector()
    static getFileDetailsDrawerState(state: FileStateModel) {
        return state.fileDetailsDrawerState;

    }

    @Selector()
    static getFileUploadDrawerState(state: FileStateModel) {
        return state.fileUploadDrawerState;
    }

    @Selector()
    static getGridDocument(state: FileStateModel) {
        return state.gridDocument;
    }

    @Action(FileManagerActions.OnViewAllFiles)
    onViewAllFiles(ctx: StateContext<FileStateModel>, { payload }: FileManagerActions.OnViewAllFiles) {
        ctx.dispatch(new FileManagerActions.SetGridDocument({
            _id: payload._id,
            documentType: payload.documentType,
            documentReference: payload.documentReference,
            documentId: payload.documentId,
        }));

        ctx.dispatch(new FileManagerActions.GetFiles());
        this._dialog.open(FileViewComponent, {
            width: '95%',
            height: '90%',
            disableClose: true
        });
    }

    @Action(FileManagerActions.UpdateGridDocument)
    updateGridDocument(ctx: StateContext<FileStateModel>, action: FileManagerActions.UpdateGridDocument) {

        const store = inject(Store);
        const gridData = store.selectSnapshot(SharedGridState.getGridConfig)[store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).firstGridUUID].rowData;
        const gridDataIndex = gridData.findIndex((row: any) => row._id === action.payload);

        const updatedGridState = produce(gridData[gridDataIndex], (draft) => {
            draft.hasFiles = true;
        });

        ctx.dispatch(new OnSetSharedGridRowData({
            gridUUID: store.selectSnapshot(SharedGridState.getGridReferenceUUIDs).firstGridUUID,
            payload: [updatedGridState],
            update: true
        }));

    }

    @Action(FileManagerActions.OnSetUploadedFiles)
    onSetUploadedFiles(ctx: StateContext<FileStateModel>, { payload }: FileManagerActions.OnSetUploadedFiles) {
        const state = ctx.getState();
        const updatedState = produce(state, (draft) => {
            // Check if the file already exists in the uploadedFiles array
            const existingFileIndex = draft.uploadedFiles.findIndex(
                item => item.file && payload.file && item.file.name === payload.file.name
            );

            if (existingFileIndex >= 0) {
                // Update existing file
                draft.uploadedFiles[existingFileIndex] = payload;
            } else {
                // Add new file
                draft.uploadedFiles.push(payload);
            }
        });
        ctx.patchState(updatedState);
    }

    @Selector()
    static getUploadedFiles(state: FileStateModel) {
        return state.uploadedFiles;
    }

    @Action(FileManagerActions.OnRemoveUploadedFiles)
    onRemoveUploadedFiles(ctx: StateContext<FileStateModel>, { payload }: FileManagerActions.OnRemoveUploadedFiles) {
        const state = ctx.getState();
        const updatedState = produce(state, (draft) => {
            draft.uploadedFiles.splice(payload, 1);
        });
        ctx.patchState(updatedState);
    }

    @Action(FileManagerActions.OnResetUploadedFiles)
    onResetUploadedFiles(ctx: StateContext<FileStateModel>) {
        ctx.patchState({ uploadedFiles: [] });
    }

}
